import { useEffect, useMemo, useState } from "react";
import {
  Plus,
  Edit2,
  Trash2,
  Star,
  X,
  Loader2,
  Link as LinkIcon,
  ExternalLink,
} from "lucide-react";
import { toast } from "sonner";
import AdminLayout from "@/components/admin/AdminLayout";
import { adminApi, formatApiError } from "@/lib/adminApi";

const CATEGORIES = [
  "Operations",
  "HR",
  "Finance",
  "Inventory",
  "Reports",
  "Legal",
  "Customer",
  "Other",
];

const KINDS = [
  "dashboard",
  "spreadsheet",
  "document",
  "folder",
  "link",
];

const UNAUTHORISED_MESSAGE =
  "You are Not Authorised for this action. Contact Admin.";

/* =========================================================
   SAFE RESOURCE RESPONSE NORMALIZER
   ========================================================= */

function normalizeResourceResponse(data) {
  /*
   Backend may return:

   1. [...]
   2. { items: [...] }
   3. { resources: [...] }
   4. { data: [...] }
   5. undefined / null

   Always return an array.
   */

  if (Array.isArray(data)) {
    return data;
  }

  if (Array.isArray(data?.items)) {
    return data.items;
  }

  if (Array.isArray(data?.resources)) {
    return data.resources;
  }

  if (Array.isArray(data?.data)) {
    return data.data;
  }

  return [];
}

/* =========================================================
   SAFE ERROR MESSAGE
   ========================================================= */

function getResourceErrorMessage(
  error,
  fallback = "Operation failed"
) {
  if (error?.response?.status === 403) {
    return (
      error?.userMessage ||
      UNAUTHORISED_MESSAGE
    );
  }

  const detail =
    error?.response?.data?.detail;

  if (
    typeof detail === "string" &&
    detail.trim()
  ) {
    return detail;
  }

  if (Array.isArray(detail)) {
    const messages = detail
      .map((item) => {
        if (typeof item === "string") {
          return item;
        }

        return item?.msg || "";
      })
      .filter(Boolean);

    if (messages.length > 0) {
      return messages.join(", ");
    }
  }

  if (
    typeof error?.userMessage === "string" &&
    error.userMessage.trim()
  ) {
    return error.userMessage;
  }

  return fallback;
}

/* =========================================================
   RESOURCES PAGE
   ========================================================= */

export default function ResourcesPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dlg, setDlg] = useState(null);
  const [filter, setFilter] = useState("all");

  /* =======================================================
     LOAD RESOURCES
     ======================================================= */

  const load = async () => {
    setLoading(true);

    try {
      const response =
        await adminApi.listResources();

      const resourceItems =
        normalizeResourceResponse(response);

      /*
       * VERY IMPORTANT:
       * Always store an array.
       * Never allow undefined to enter state.
       */
      setItems(resourceItems);
    } catch (error) {
      console.error(
        "Resources load failed:",
        error
      );

      setItems([]);

      toast.error(
        getResourceErrorMessage(
          error,
          "Could not load resources"
        )
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  /* =======================================================
     DELETE
     ======================================================= */

  const remove = async (resource) => {
    if (
      !window.confirm(
        `Delete "${resource?.title || "this resource"}"?`
      )
    ) {
      return;
    }

    try {
      await adminApi.deleteResource(
        resource.id
      );

      toast.success("Deleted");

      await load();
    } catch (error) {
      console.error(
        "Resource delete failed:",
        error
      );

      toast.error(
        getResourceErrorMessage(
          error,
          "Delete failed"
        )
      );
    }
  };

  /* =======================================================
     STAR
     ======================================================= */

  const toggleStar = async (resource) => {
    try {
      await adminApi.updateResource(
        resource.id,
        {
          starred: !Boolean(
            resource.starred
          ),
        }
      );

      await load();
    } catch (error) {
      console.error(
        "Resource star update failed:",
        error
      );

      toast.error(
        getResourceErrorMessage(
          error,
          "Could not update resource"
        )
      );
    }
  };

  /* =======================================================
     ENABLE / DISABLE
     ======================================================= */

  const toggleEnabled = async (resource) => {
    try {
      await adminApi.updateResource(
        resource.id,
        {
          enabled: !(
            resource.enabled ?? true
          ),
        }
      );

      await load();
    } catch (error) {
      console.error(
        "Resource enable update failed:",
        error
      );

      toast.error(
        getResourceErrorMessage(
          error,
          "Could not update resource"
        )
      );
    }
  };

  /* =======================================================
     FILTERED RESOURCES
     ======================================================= */

  const filtered = useMemo(() => {
    const safeItems = Array.isArray(items)
      ? items
      : [];

    if (filter === "all") {
      return safeItems;
    }

    return safeItems.filter(
      (item) =>
        item?.category === filter
    );
  }, [items, filter]);

  /* =======================================================
     SAFE COUNTS
     ======================================================= */

  const totalCount = Array.isArray(items)
    ? items.length
    : 0;

  /* =======================================================
     UI
     ======================================================= */

  return (
    <AdminLayout
      title="Resources"
      subtitle="External links, Google Sheets, dashboards & documents shown on the workspace."
    >
      {/* ===================================================
          FILTERS / ADD BUTTON
          =================================================== */}

      <div className="flex flex-wrap items-center gap-2">
        <button
          onClick={() =>
            setFilter("all")
          }
          className={`text-xs px-3 py-1.5 rounded-full ${
            filter === "all"
              ? "bg-foreground text-background"
              : "border border-border hover:bg-muted"
          }`}
        >
          All ({totalCount})
        </button>

        {CATEGORIES.map((category) => {
          const safeItems = Array.isArray(
            items
          )
            ? items
            : [];

          const count =
            safeItems.filter(
              (item) =>
                item?.category ===
                category
            ).length;

          if (!count) {
            return null;
          }

          return (
            <button
              key={category}
              onClick={() =>
                setFilter(category)
              }
              className={`text-xs px-3 py-1.5 rounded-full ${
                filter === category
                  ? "bg-foreground text-background"
                  : "border border-border hover:bg-muted"
              }`}
            >
              {category} ({count})
            </button>
          );
        })}

        <div className="ml-auto">
          <button
            data-testid="new-resource"
            onClick={() =>
              setDlg({
                mode: "new",
              })
            }
            className="inline-flex items-center gap-1.5 h-9 px-4 rounded-full bg-foreground text-background text-sm font-semibold"
          >
            <Plus className="w-4 h-4" />
            Add Resource
          </button>
        </div>
      </div>

      {/* ===================================================
          RESOURCE TABLE / CARDS
          =================================================== */}

      <div className="rounded-2xl border border-border bg-card overflow-hidden">
        {/* =================================================
            DESKTOP TABLE
            ================================================= */}

        <table className="w-full text-sm hidden md:table">
          <thead className="bg-muted/50 text-[10px] uppercase tracking-wider">
            <tr>
              <th className="px-3 py-2 text-left">
                #
              </th>

              <th className="px-3 py-2 text-left">
                Title
              </th>

              <th className="px-3 py-2 text-left">
                Category
              </th>

              <th className="px-3 py-2 text-left">
                Kind
              </th>

              <th className="px-3 py-2 text-left">
                URL
              </th>

              <th className="px-3 py-2 text-left">
                Enabled
              </th>

              <th className="px-3 py-2"></th>
            </tr>
          </thead>

          <tbody>
            {/* Loading */}

            {loading && (
              <tr>
                <td
                  colSpan={7}
                  className="text-center py-6"
                >
                  <Loader2 className="w-4 h-4 inline animate-spin" />
                </td>
              </tr>
            )}

            {/* Empty */}

            {!loading &&
              filtered.length === 0 && (
                <tr>
                  <td
                    colSpan={7}
                    className="text-center py-6 text-muted-foreground text-sm"
                  >
                    No resources
                  </td>
                </tr>
              )}

            {/* Rows */}

            {!loading &&
              filtered.map((resource) => (
                <tr
                  key={
                    resource?.id ||
                    resource?._id ||
                    resource?.sno
                  }
                  className="border-t border-border hover:bg-muted/30"
                >
                  <td className="px-3 py-2 tabular-nums text-xs text-muted-foreground">
                    {resource?.sno ??
                      "-"}
                  </td>

                  <td className="px-3 py-2">
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() =>
                          toggleStar(
                            resource
                          )
                        }
                        className="text-muted-foreground hover:text-amber-500"
                      >
                        <Star
                          className={`w-3.5 h-3.5 ${
                            resource?.starred
                              ? "fill-amber-500 text-amber-500"
                              : ""
                          }`}
                        />
                      </button>

                      <span className="font-semibold">
                        {resource?.title ||
                          "Untitled"}
                      </span>
                    </div>

                    <div className="text-[11px] text-muted-foreground line-clamp-1">
                      {resource?.description ||
                        ""}
                    </div>
                  </td>

                  <td className="px-3 py-2 text-xs">
                    {resource?.category ||
                      "-"}
                  </td>

                  <td className="px-3 py-2 text-xs uppercase text-muted-foreground">
                    {resource?.kind ||
                      "-"}
                  </td>

                  <td className="px-3 py-2">
                    {resource?.url ? (
                      <a
                        href={
                          resource.url
                        }
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-xs text-[hsl(var(--primary))] hover:underline inline-flex items-center gap-1 max-w-[240px] truncate"
                      >
                        <ExternalLink className="w-3 h-3 shrink-0" />

                        <span className="truncate">
                          {resource.url}
                        </span>
                      </a>
                    ) : (
                      <span className="text-xs text-muted-foreground">
                        -
                      </span>
                    )}
                  </td>

                  <td className="px-3 py-2">
                    <button
                      onClick={() =>
                        toggleEnabled(
                          resource
                        )
                      }
                      className={`inline-flex px-2 py-0.5 rounded-full text-[10px] font-bold ${
                        resource?.enabled ??
                        true
                          ? "bg-emerald-500/10 text-emerald-600"
                          : "bg-muted text-muted-foreground"
                      }`}
                    >
                      {(resource?.enabled ??
                        true)
                        ? "enabled"
                        : "disabled"}
                    </button>
                  </td>

                  <td className="px-3 py-2 text-right whitespace-nowrap">
                    <button
                      onClick={() =>
                        setDlg({
                          mode: "edit",
                          resource,
                        })
                      }
                      className="w-8 h-8 rounded-md hover:bg-muted inline-flex items-center justify-center"
                    >
                      <Edit2 className="w-3.5 h-3.5" />
                    </button>

                    <button
                      onClick={() =>
                        remove(resource)
                      }
                      className="w-8 h-8 rounded-md hover:bg-[hsl(var(--destructive))]/10 hover:text-[hsl(var(--destructive))] inline-flex items-center justify-center"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </td>
                </tr>
              ))}
          </tbody>
        </table>

        {/* =================================================
            MOBILE CARDS
            ================================================= */}

        <div className="md:hidden divide-y divide-border">
          {loading && (
            <div className="p-6 text-center">
              <Loader2 className="w-4 h-4 inline animate-spin" />
            </div>
          )}

          {!loading &&
            filtered.length === 0 && (
              <div className="p-6 text-center text-sm text-muted-foreground">
                No resources
              </div>
            )}

          {!loading &&
            filtered.map((resource) => (
              <div
                key={
                  resource?.id ||
                  resource?._id ||
                  resource?.sno
                }
                className="p-3"
              >
                <div className="flex items-start gap-2">
                  <button
                    onClick={() =>
                      toggleStar(
                        resource
                      )
                    }
                  >
                    <Star
                      className={`w-4 h-4 mt-1 ${
                        resource?.starred
                          ? "fill-amber-500 text-amber-500"
                          : "text-muted-foreground"
                      }`}
                    />
                  </button>

                  <div className="flex-1 min-w-0">
                    <div className="font-semibold truncate">
                      {resource?.title ||
                        "Untitled"}
                    </div>

                    <div className="text-[11px] text-muted-foreground line-clamp-2">
                      {resource?.description ||
                        ""}
                    </div>

                    <div className="mt-1 flex items-center gap-2 text-[10px]">
                      <span className="bg-muted px-2 py-0.5 rounded-full font-bold uppercase">
                        {resource?.category ||
                          "-"}
                      </span>

                      <span className="text-muted-foreground">
                        {resource?.kind ||
                          "-"}
                      </span>

                      <button
                        onClick={() =>
                          toggleEnabled(
                            resource
                          )
                        }
                        className={`ml-auto inline-flex px-2 py-0.5 rounded-full font-bold ${
                          resource?.enabled ??
                          true
                            ? "bg-emerald-500/10 text-emerald-600"
                            : "bg-muted text-muted-foreground"
                        }`}
                      >
                        {(resource?.enabled ??
                          true)
                          ? "enabled"
                          : "disabled"}
                      </button>
                    </div>

                    <div className="mt-2 flex gap-1">
                      <button
                        onClick={() =>
                          setDlg({
                            mode: "edit",
                            resource,
                          })
                        }
                        className="flex-1 text-xs py-1.5 rounded-lg border border-border inline-flex items-center justify-center gap-1"
                      >
                        <Edit2 className="w-3 h-3" />
                        Edit
                      </button>

                      <button
                        onClick={() =>
                          remove(resource)
                        }
                        className="flex-1 text-xs py-1.5 rounded-lg border border-border hover:bg-[hsl(var(--destructive))]/10 hover:text-[hsl(var(--destructive))] inline-flex items-center justify-center gap-1"
                      >
                        <Trash2 className="w-3 h-3" />
                        Delete
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
        </div>
      </div>

      {/* ===================================================
          RESOURCE DIALOG
          =================================================== */}

      {dlg && (
        <ResourceDialog
          mode={dlg.mode}
          resource={dlg.resource}
          onClose={() =>
            setDlg(null)
          }
          onSaved={() => {
            setDlg(null);
            load();
          }}
        />
      )}
    </AdminLayout>
  );
}

/* =========================================================
   RESOURCE DIALOG
   ========================================================= */

function ResourceDialog({
  mode,
  resource,
  onClose,
  onSaved,
}) {
  const isEdit = mode === "edit";

  const [f, setF] = useState(() => ({
    title:
      resource?.title || "",

    description:
      resource?.description || "",

    category:
      resource?.category ||
      "Operations",

    kind:
      resource?.kind ||
      "document",

    url:
      resource?.url || "",

    starred:
      Boolean(resource?.starred),

    enabled:
      resource?.enabled ?? true,
  }));

  const [busy, setBusy] =
    useState(false);

  /* =======================================================
     SAVE
     ======================================================= */

  const save = async () => {
    if (!f.title.trim()) {
      toast.error(
        "Title is required"
      );
      return;
    }

    if (!f.category.trim()) {
      toast.error(
        "Category is required"
      );
      return;
    }

    if (!f.url.trim()) {
      toast.error(
        "URL is required"
      );
      return;
    }

    setBusy(true);

    try {
      if (isEdit) {
        await adminApi.updateResource(
          resource.id,
          f
        );
      } else {
        await adminApi.addResource(
          f
        );
      }

      toast.success(
        isEdit
          ? "Updated"
          : "Added"
      );

      onSaved();
    } catch (error) {
      console.error(
        "Resource save failed:",
        error
      );

      toast.error(
        getResourceErrorMessage(
          error,
          "Could not save resource"
        )
      );
    } finally {
      setBusy(false);
    }
  };

  /* =======================================================
     DIALOG UI
     ======================================================= */

  return (
    <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="w-full max-w-xl bg-card border border-border rounded-2xl overflow-hidden max-h-[92vh] flex flex-col">
        {/* Header */}

        <div className="flex items-center justify-between p-4 border-b border-border">
          <h3 className="font-heading text-lg font-bold">
            {isEdit
              ? "Edit Resource"
              : "Add Resource"}
          </h3>

          <button
            onClick={onClose}
            className="w-9 h-9 rounded-md hover:bg-muted flex items-center justify-center"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Body */}

        <div className="flex-1 overflow-y-auto p-5 space-y-3">
          {/* Title */}

          <div>
            <label className="text-[10px] uppercase tracking-wider font-bold text-muted-foreground">
              Title*
            </label>

            <input
              data-testid="res-title"
              value={f.title}
              onChange={(e) =>
                setF((state) => ({
                  ...state,
                  title:
                    e.target.value,
                }))
              }
              className="mt-1 w-full h-10 px-3 rounded-lg bg-background border border-border text-sm focus:outline-none focus:ring-2 focus:ring-[hsl(var(--primary))]"
            />
          </div>

          {/* Description */}

          <div>
            <label className="text-[10px] uppercase tracking-wider font-bold text-muted-foreground">
              Description
            </label>

            <textarea
              value={f.description}
              onChange={(e) =>
                setF((state) => ({
                  ...state,
                  description:
                    e.target.value,
                }))
              }
              rows={2}
              className="mt-1 w-full px-3 py-2 rounded-lg bg-background border border-border text-sm focus:outline-none focus:ring-2 focus:ring-[hsl(var(--primary))]"
            />
          </div>

          {/* Category / Kind */}

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-[10px] uppercase tracking-wider font-bold text-muted-foreground">
                Category*
              </label>

              <select
                value={f.category}
                onChange={(e) =>
                  setF((state) => ({
                    ...state,
                    category:
                      e.target.value,
                  }))
                }
                className="mt-1 w-full h-10 px-3 rounded-lg bg-background border border-border text-sm"
              >
                {CATEGORIES.map(
                  (category) => (
                    <option
                      key={category}
                      value={category}
                    >
                      {category}
                    </option>
                  )
                )}
              </select>
            </div>

            <div>
              <label className="text-[10px] uppercase tracking-wider font-bold text-muted-foreground">
                Kind
              </label>

              <select
                value={f.kind}
                onChange={(e) =>
                  setF((state) => ({
                    ...state,
                    kind:
                      e.target.value,
                  }))
                }
                className="mt-1 w-full h-10 px-3 rounded-lg bg-background border border-border text-sm"
              >
                {KINDS.map((kind) => (
                  <option
                    key={kind}
                    value={kind}
                  >
                    {kind}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* URL */}

          <div>
            <label className="text-[10px] uppercase tracking-wider font-bold text-muted-foreground">
              URL*
            </label>

            <div className="mt-1 relative">
              <LinkIcon className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />

              <input
                data-testid="res-url"
                value={f.url}
                onChange={(e) =>
                  setF((state) => ({
                    ...state,
                    url:
                      e.target.value,
                  }))
                }
                placeholder="https://…"
                className="w-full h-10 pl-9 pr-3 rounded-lg bg-background border border-border text-sm focus:outline-none focus:ring-2 focus:ring-[hsl(var(--primary))]"
              />
            </div>
          </div>

          {/* Options */}

          <div className="flex items-center gap-4">
            <label className="inline-flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={Boolean(
                  f.starred
                )}
                onChange={(e) =>
                  setF((state) => ({
                    ...state,
                    starred:
                      e.target
                        .checked,
                  }))
                }
              />

              Starred
            </label>

            <label className="inline-flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={Boolean(
                  f.enabled
                )}
                onChange={(e) =>
                  setF((state) => ({
                    ...state,
                    enabled:
                      e.target
                        .checked,
                  }))
                }
              />

              Enabled
            </label>
          </div>
        </div>

        {/* Footer */}

        <div className="p-4 border-t border-border flex justify-end gap-2">
          <button
            onClick={onClose}
            className="px-4 h-10 rounded-lg border border-border text-sm"
          >
            Cancel
          </button>

          <button
            data-testid="res-save"
            onClick={save}
            disabled={busy}
            className="px-4 h-10 rounded-lg bg-foreground text-background text-sm font-semibold inline-flex items-center gap-1.5 disabled:opacity-50"
          >
            {busy && (
              <Loader2 className="w-4 h-4 animate-spin" />
            )}

            Save
          </button>
        </div>
      </div>
    </div>
  );
}